import { useEffect, useId } from 'react';
import { Button } from '@vd/ui';
import { useTours } from './TourProvider';
import type { TourOffer } from './tours';
import styles from './TourLaunch.module.css';

const LABELS: Record<TourOffer['kind'], string> = {
  start: 'Start',
  resume: 'Resume',
  replay: 'Replay',
  changed: 'Replay (updated)',
  elsewhere: 'Start here',
};

/**
 * Starts a tour, labelled by what it offers this person here (TOADO-971).
 * Nothing at all where tours are off or the tour is not registered.
 */
export function TourLaunch({ tourId, label, primary = false }: { tourId: string; label?: string; primary?: boolean }) {
  const tours = useTours();
  const offer = tours.offerFor(tourId);
  const reasonId = useId();
  const { registerLaunch } = tours;
  useEffect(() => registerLaunch(), [registerLaunch]);
  if (!tours.enabled || !offer) return null;
  const busy = tours.active !== null;
  // While it cannot start a tour it is marked so (aria-disabled) rather than
  // disabled, which would take it out of the tab order: focused, it is read
  // out with its reason. The reason is also a tooltip on the wrapper, for the pointer.
  const why = tours.recording ? 'Tours wait until the recording has finished.' : tours.underModal ? 'Tours wait until the open dialog is closed.' : undefined;
  const blocked = busy || !!why;
  return (
    <span className={styles.launch} title={why} data-tour-launch-why={why ? '' : undefined}>
    <Button
      // Never a form's submit: the shared Button sets no type, and a blocked
      // launch is pressable (aria-disabled), so a form around it must not hear it.
      type="button"
      size={primary ? 'md' : 'sm'}
      variant={primary ? 'primary' : 'secondary'}
      aria-disabled={blocked ? 'true' : undefined}
      aria-describedby={why ? reasonId : undefined}
      onClick={(event) => {
        if (blocked) {
          event.preventDefault();
          return;
        }
        tours.start(tourId);
      }}
      aria-label={label ? `${LABELS[offer.kind]} ${label}` : undefined}
      data-tour-launch={tourId}
    >
      {LABELS[offer.kind]}
    </Button>
    {/* A tour closed partway can also be begun again (Jason, 28 Oct). */}
    {offer.kind === 'resume' && (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-disabled={blocked ? 'true' : undefined}
        aria-describedby={why ? reasonId : undefined}
        onClick={(event) => {
          if (blocked) {
            event.preventDefault();
            return;
          }
          tours.start(tourId, true);
        }}
        aria-label={label ? `Start over ${label}` : undefined}
        data-tour-restart={tourId}
      >
        Start over
      </Button>
    )}
    {why && <span id={reasonId} className={styles.reason}>{why}</span>}
    </span>
  );
}

/** Every tour there is, each with its own launch, for Get started (TOADO-971); less one shown on its own above it. */
export function ToursList({ except }: { except?: string }) {
  const tours = useTours();
  const listed = tours.tours.filter((tour) => tour.id !== except);
  if (!tours.enabled || listed.length === 0) return null;
  return (
    <section className={styles.list} aria-labelledby="tours-list-label" data-testid="tours-list">
      <h3 id="tours-list-label" className={styles.label}>Tours</h3>
      <ul className={styles.items}>
        {listed.map((tour) => (
          <li key={tour.id} className={styles.item}>
            <span>{tour.title}</span>
            <TourLaunch tourId={tour.id} label={tour.title} />
          </li>
        ))}
      </ul>
    </section>
  );
}
